import React, { useMemo }  from "react";

import AccordionItem from "../custom-visual-inspection/accordion-item";
import CustomNewItem from "../custom-general/custom-new-item";
import Conditional from "../../common/conditional";
import SWUTItemForm, { SWUTItem } from "../../forms/custom-swut/swutItemForm";
import SWUTObservationItemForm, { SWUTObservationItem } from "../../forms/custom-swut/swutObservationItem";
import SWUTProbeItemForm, { SWUTProbeItem } from "../../forms/custom-swut/swutProbeItem";
import WarningNotice from "../custom-visual-inspection/warning-notice";
import _ from "lodash";

interface Props {
    items: SWUTItem[];
    onDataChange: React.Dispatch<React.SetStateAction<SWUTItem[]>>;
}

const SWUTReportItems: React.FC<Props> = ({ items, onDataChange }) => {
    const handleAddSWUTData = (swReport: SWUTItem) => {
        onDataChange((previous) => [...previous, { ...swReport, id: previous.length + 1, observations: [], probes: [] }]);
    };

    const handleAddSWUTObservationItem = (swutItem: SWUTItem, observation: SWUTObservationItem) => {
        const newObservation = {
            ...observation,
            id: swutItem.observations.length + 1,
        };

        onDataChange((previous) => {
            return previous.map((report) => report.id === swutItem.id ? {
                ...swutItem,
                observations: [...swutItem.observations, newObservation],
            } : report);
        });
    };

    const handleAddSWUTProbeItem = (swutItem: SWUTItem, probe: SWUTProbeItem) => {
        const newProbe = {
            ...probe,
            id: swutItem.probes.length + 1,
        };

        onDataChange((previous) => {
            return previous.map((report) => report.id === swutItem.id ? {
                ...swutItem,
                probes: [...swutItem.probes, newProbe],
            } : report);
        });
    };

    const handleEditSWUTData = (originalReport: SWUTItem, updatedReport: SWUTItem) => {
        onDataChange((previous) => previous.map((report) => report.id === originalReport.id ? updatedReport : report));
    };

    const handleEditSWUTObservationItem = (swutItem: SWUTItem, observationToEdit: SWUTObservationItem) => {
        onDataChange((previous) => {
            return previous.map((report) => report.id === swutItem.id ? {
                ...swutItem,
                observations: swutItem.observations.map((observation) => observation.id === observationToEdit.id ? observationToEdit : observation),
            } : report);
        });
    };
    
    const handleEditSWUTProbeItem = (swutItem: SWUTItem, probeToEdit: SWUTProbeItem) => {
        onDataChange((previous) => {
            return previous.map((report) => report.id === swutItem.id ? {
                ...swutItem,
                probes: swutItem.probes.map((probe) => probe.id === probeToEdit.id ? probeToEdit : probe),
            } : report);
        });
    };

    const handleDeleteSWUTData = (report: SWUTItem) => {
        onDataChange((previous) => previous.filter((swReport) => swReport.id !== report.id));
    };

    const handleDeleteSWUTObservationItem = (swutItem: SWUTItem, observationToDelete: SWUTObservationItem) => {
        onDataChange((previous) => {
            return previous.map((report) => report.id === swutItem.id ? {
                ...swutItem,
                observations: swutItem.observations.filter((observation) => observation.id !== observationToDelete.id),
            } : report);
        });
    };
    
    const handleDeleteSWUTProbeItem = (swutItem: SWUTItem, probeToDelete: SWUTProbeItem) => {
        onDataChange((previous) => {
            return previous.map((report) => report.id === swutItem.id ? {
                ...swutItem,
                probes: swutItem.probes.filter((probe) => probe.id !== probeToDelete.id),
            } : report);
        });
    };


    const weldsWithInsufficientProbes = useMemo(() => {
        return items.filter((item) => item.probes.length < 3);
    }, [items]);

    return ( 
        <>
            <WarningNotice
                title="Incomplete data"
                body={`There must be at least 3 probe angles per weld for ${weldsWithInsufficientProbes.map((weld) => weld.weldID).join(', ')}`}
            />
            
            <Conditional isVisible={weldsWithInsufficientProbes.length === 0}>
                <CustomNewItem label='Add New SWUT Item'>
                    <SWUTItemForm onSave={handleAddSWUTData} />
                </CustomNewItem>
            </Conditional>

            <Conditional isVisible={items.length > 0}>
                <div className="bg-light p-2 rounded border">
                    {items.map((report) => (
                        <AccordionItem key={report.id} isActive title={report.weldID}>
                            <div className="bg-white p-2 rounded border">
                                <SWUTItemForm
                                    initialSWUTItem={report}
                                    onSave={(newReport: SWUTItem) => handleEditSWUTData(report, newReport)}
                                    onDeleteItem={() => handleDeleteSWUTData(report)}
                                />
                            </div>

                            <div className="bg-white p-2 rounded border mt-4">
                                <AccordionItem isActive title={"Welds for " + report.weldID}>
                                    <CustomNewItem label='Add New Weld'>
                                        <SWUTObservationItemForm onSave={(observation: SWUTObservationItem) => handleAddSWUTObservationItem(report, observation)} />
                                    </CustomNewItem>

                                    <Conditional isVisible={report.observations.length > 0}>
                                        {report.observations.map((observation) => (
                                            <AccordionItem isActive key={observation.id} title={observation.jointNumber}>
                                                <SWUTObservationItemForm
                                                    initialObservationItem={observation}
                                                    onSave={(updatedObservation: SWUTObservationItem) => handleEditSWUTObservationItem(report, updatedObservation)}
                                                    onDeleteItem={() => handleDeleteSWUTObservationItem(report, observation)}
                                                />
                                            </AccordionItem>
                                        ))}
                                    </Conditional>
                                </AccordionItem>
                            </div>
                            
                            <div className="bg-white p-2 rounded border mt-4">
                                <AccordionItem isActive title={"Probes for " + report.weldID}>
                                    <CustomNewItem label='Add New SWUT Probe'>
                                        <SWUTProbeItemForm
                                            probeAngles={_.difference(PROBE_ANGLES, report.probes.map((probe) => +probe.angle))}
                                            onSave={(probe: SWUTProbeItem) => handleAddSWUTProbeItem(report, probe)}
                                        />
                                    </CustomNewItem>

                                    <Conditional isVisible={report.probes.length > 0}>
                                        {report.probes.map((probe) => (
                                            <AccordionItem isActive key={probe.id} title={probe.id.toString()}>
                                                <SWUTProbeItemForm
                                                    initialProbeItem={probe}
                                                    probeAngles={_.difference(PROBE_ANGLES, report.probes.filter((reportProbe) => reportProbe.id !== probe.id).map((probe) => +probe.angle))}
                                                    onSave={(updatedProbe: SWUTProbeItem) => handleEditSWUTProbeItem(report, updatedProbe)}
                                                    onDeleteItem={() => handleDeleteSWUTProbeItem(report, probe)}
                                                />
                                            </AccordionItem>
                                        ))}
                                    </Conditional>
                                </AccordionItem>
                            </div>
                        </AccordionItem>
                    ))}
                </div>
            </Conditional>    
        </>
    );
};

const PROBE_ANGLES = [0, 45, 60, 70];
 
export default SWUTReportItems;